import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { showSuccess, showError } from "toast/toast";
import { getAuthCookie } from "utils/authCookies";
import {
    createEmailTemplate,
    extractEmailTemplateStorageFileId,
    fetchEmailTemplateById,
    hardDeleteEmailTemplateStorageFile,
    updateEmailTemplate,
    uploadEmailTemplateStorageFile,
} from "components/emailtempleateapicalls";

/** Dummy seed data until API wiring exists */
const DUMMY_EMAIL_TEMPLATE = {
    customerMessage:
        "Thank you for choosing us! We appreciate your business and look forward to serving you.",
    linkedin: "https://linkedin.com/company/your-company",
    facebook: "https://facebook.com/your-page",
    instagram: "https://instagram.com/your-handle",
    twitter: "https://x.com/your-handle",
};

type FormState = typeof DUMMY_EMAIL_TEMPLATE & { logoPreview: string | null; logoName: string };

function SocialUrlRow({
    id,
    label,
    iconClass,
    badgeStyle,
    placeholder,
    value,
    onChange,
}: {
    id: string;
    label: string;
    iconClass: string;
    badgeStyle: React.CSSProperties;
    placeholder: string;
    value: string;
    onChange: (v: string) => void;
}) {
    return (
        <div className="social-field-row mb-4">
            <label className="social-field-label d-flex align-items-center gap-2 mb-2" htmlFor={id}>
                <i className={iconClass} aria-hidden />
                {label}
            </label>
            <div className="social-input-group">
                <div className="social-input-prefix" style={badgeStyle}>
                    <i className={iconClass} aria-hidden />
                </div>
                <input
                    id={id}
                    type="url"
                    className="social-input-field"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    autoComplete="url"
                />
            </div>
        </div>
    );
}

export default function Signageemailteampleate() {
    const { pathname, search } = useLocation();
    const navigate = useNavigate();
    const base = pathname.startsWith("/signageteammember") ? "/signageteammember" : "/signage";
    const token = getAuthCookie("sessionToken") || "";
    const templateId = React.useMemo(() => {
        const qs = new URLSearchParams(search);
        return qs.get("templateId") || qs.get("templated") || "";
    }, [search]);
    const isEditMode = Boolean(templateId);

    const [form, setForm] = React.useState<FormState>(() => ({
        ...DUMMY_EMAIL_TEMPLATE,
        logoPreview: null as string | null,
        logoName: "",
    }));
    const [makeDefaultTemplate, setMakeDefaultTemplate] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [initialLoading, setInitialLoading] = React.useState(false);
    const [uploadingLogo, setUploadingLogo] = React.useState(false);
    const [deletingLogo, setDeletingLogo] = React.useState(false);
    const [logoFileId, setLogoFileId] = React.useState<string>("");
    React.useEffect(() => {
        let cancelled = false;
        const loadTemplate = async () => {
            if (!isEditMode || !token) return;
            setInitialLoading(true);
            try {
                const data = await fetchEmailTemplateById(token, templateId);
                if (cancelled) return;
                const social = data?.social_links || {};
                setForm((prev) => ({
                    ...prev,
                    customerMessage: String(data?.customer_message || ""),
                    linkedin: String(social.linkedin || ""),
                    facebook: String(social.facebook || ""),
                    instagram: String(social.instagram || ""),
                    twitter: String(social.twitter || ""),
                    logoPreview: String(data?.company_logo_presigned_url || ""),
                    logoName: "",
                }));
                setLogoFileId(String(data?.company_logo || ""));
                setMakeDefaultTemplate(
                    typeof data?.default === "boolean"
                        ? data.default
                        : Boolean((data as any)?.is_default)
                );
            } catch (error) {
                if (!cancelled) {
                    showError(error instanceof Error ? error.message : "Failed to load email template.");
                }
            } finally {
                if (!cancelled) setInitialLoading(false);
            }
        };
        loadTemplate();
        return () => {
            cancelled = true;
        };
    }, [isEditMode, templateId, token]);

    const fileInputRef = React.useRef<HTMLInputElement | null>(null);

    const setField = (key: keyof typeof DUMMY_EMAIL_TEMPLATE, value: string) => {
        setForm((prev) => ({ ...prev, [key]: value }));
    };

    const onChooseFile = () => fileInputRef.current?.click();

    const onFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            showError("Please select a valid image file.");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (f.size > 5 * 1024 * 1024) {
            showError("File size must be 5 MB or less.");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        if (form.logoPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(form.logoPreview);
        }
        const blobUrl = URL.createObjectURL(f);
        setForm((prev) => ({ ...prev, logoPreview: blobUrl, logoName: f.name }));
        if (!token) {
            showError("Missing auth token. Please login again.");
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }
        setUploadingLogo(true);
        try {
            if (logoFileId) {
                await hardDeleteEmailTemplateStorageFile(token, logoFileId);
            }
            const uploadRaw = await uploadEmailTemplateStorageFile(token, f);
            const uploadedId = extractEmailTemplateStorageFileId(uploadRaw);
            if (!uploadedId) throw new Error("Logo uploaded but id missing from response.");
            setLogoFileId(uploadedId);
            showSuccess("Logo uploaded successfully.");
        } catch (error) {
            if (blobUrl.startsWith("blob:")) URL.revokeObjectURL(blobUrl);
            setForm((prev) => ({ ...prev, logoPreview: null, logoName: "" }));
            setLogoFileId("");
            showError(error instanceof Error ? error.message : "Failed to upload logo.");
        } finally {
            setUploadingLogo(false);
            // After await, e.currentTarget is null — clear via ref instead.
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const removeLogo = async () => {
        if (!token) {
            showError("Missing auth token. Please login again.");
            return;
        }
        setDeletingLogo(true);
        try {
            if (logoFileId) {
                await hardDeleteEmailTemplateStorageFile(token, logoFileId);
            }
            setLogoFileId("");
            showSuccess("Logo removed.");
        } catch (error) {
            showError(error instanceof Error ? error.message : "Failed to remove logo.");
            setDeletingLogo(false);
            return;
        }
        if (form.logoPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(form.logoPreview);
        }
        setForm((prev) => ({ ...prev, logoPreview: null, logoName: "" }));
        if (fileInputRef.current) fileInputRef.current.value = "";
        setDeletingLogo(false);
    };

    const handleSubmit = async (ev: React.FormEvent) => {
        ev.preventDefault();
        if (!String(logoFileId || "").trim()) {
            showError("Company logo is required.");
            return;
        }
        // if (!form.customerMessage.trim()) {
        //     showError("Message is required.");
        //     return;
        // }
        if (!token) {
            showError("Missing auth token. Please login again.");
            return;
        }
        setSaving(true);
        try {
            const social_links = {
                linkedin: form.linkedin,
                facebook: form.facebook,
                instagram: form.instagram,
                twitter: form.twitter,
            };
            if (isEditMode) {
                await updateEmailTemplate(token, templateId, {
                    company_logo: logoFileId,
                    customer_message: form.customerMessage,
                    is_default: makeDefaultTemplate,
                    social_links,
                });
                showSuccess("Email template updated successfully.");
            } else {
                await createEmailTemplate(token, {
                    company_logo: logoFileId,
                    customer_message: form.customerMessage,
                    is_default: makeDefaultTemplate,
                    social_links,
                });
                showSuccess("Email template saved successfully.");
            }
            navigate(`${base}/SignageemailTeampleates`);
        } catch (error) {
            showError(error instanceof Error ? error.message : "Failed to save email template.");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        if (form.logoPreview?.startsWith("blob:")) {
            URL.revokeObjectURL(form.logoPreview);
        }
        setForm({
            ...DUMMY_EMAIL_TEMPLATE,
            logoPreview: null,
            logoName: "",
        });
        setLogoFileId("");
        setMakeDefaultTemplate(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
        navigate(`${base}/SignageemailTeampleates`);
    };

    return (
        <div className="container my-4">
            <div className="col-lg-9">
                <div className="d-flex align-items-start gap-2 mb-3">
                    <div
                        className="rounded-3 d-inline-flex align-items-center justify-content-center"
                        style={{ width: 40, height: 40, background: "#EAF1FF", color: "#1E66FF" }}
                    >
                        <i className="bi bi-envelope" style={{ fontSize: "1.15rem" }} />
                    </div>
                    <div>
                        <h4 className="mb-0">{isEditMode ? "Edit Email Template" : "Email Template"}</h4>
                        <div className="text-muted">Configure your branding and content for outgoing emails</div>
                    </div>
                </div>

                {/* <nav className="small text-muted mb-4" aria-label="Breadcrumb">
                    <Link to={`${base}/welcome`} className="text-muted text-decoration-none">
                        Dashboard
                    </Link>
                    <span className="mx-1">/</span>
                    <Link to={`${base}/generalSettings`} className="text-muted text-decoration-none">
                        Settings
                    </Link>
                    <span className="mx-1">/</span>
                    <span className="text-secondary fw-medium">Email Template</span>
                </nav> */}

                <form onSubmit={handleSubmit}>
                    {initialLoading && (
                        <div className="alert alert-info mb-3">Loading template details...</div>
                    )}
                    {/* Branding */}
                    <div className="card shadow-lg mb-4 template-card">
                        <div className="card-body">
                            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-1">
                                <i className="bi bi-image" style={{ color: "#1E66FF" }} />
                                Branding
                            </h6>
                            <p className="text-muted small mb-3">Upload your company logo to display in email headers</p>
                            <label className="form-label small fw-semibold">
                                Company Logo <span className="text-danger">*</span>
                            </label>

                            {!form.logoPreview ? (
                                <div className="dropzone d-flex flex-column align-items-center justify-content-center text-center">
                                    <div className="display-6 text-secondary mb-2">
                                        <i className="bi bi-cloud-arrow-up" />
                                    </div>
                                    <div className="fw-semibold">Upload your logo</div>
                                    <div className="text-muted small">PNG, JPG, or SVG up to 5 MB</div>
                                    <button type="button" className="btn btn-light border mt-3" onClick={onChooseFile} disabled={uploadingLogo}>
                                        <i className="bi bi-upload me-2" />
                                        {uploadingLogo ? "Uploading..." : "Choose File"}
                                    </button>
                                </div>
                            ) : (
                                <div className="logo-preview-wrap d-flex align-items-center justify-content-center">
                                    <div className="logo-preview position-relative d-inline-block">
                                        <img
                                            src={form.logoPreview}
                                            alt="Logo preview"
                                            className="rounded w-100 h-100 object-fit-contain"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light border position-absolute top-0 end-0 m-1 rounded-circle p-0"
                                            style={{ width: 28, height: 28, lineHeight: 1 }}
                                            onClick={removeLogo}
                                            title="Remove"
                                            disabled={deletingLogo || uploadingLogo}
                                        >
                                            <i className="bi bi-x-lg small" />
                                        </button>
                                    </div>
                                </div>
                            )}
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/svg+xml"
                                className="d-none"
                                onChange={onFileChange}
                            />
                        </div>
                    </div>

                    {/* Customer message */}
                    <div className="card shadow-lg mb-4 template-card">
                        <div className="card-body">
                            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-1">
                                <i className="bi bi-chat-right-text" style={{ color: "#1E66FF" }} />
                                Customer Message
                            </h6>
                            <p className="text-muted small mb-3">
                                A personalised message displayed in every email sent to your customers
                            </p>
                            <label className="form-label" htmlFor="customerMessage">
                                Message
                                 {/* <span className="text-danger">*</span> */}
                            </label>
                            <textarea
                                id="customerMessage"
                                className="form-control always-border"
                                rows={5}
                                placeholder="e.g. Thank you for choosing us! We appreciate your business and look forward to serving you."
                                value={form.customerMessage}
                                onChange={(e) => setField("customerMessage", e.target.value)}
                            />
                            <div className="text-muted small mt-2">
                                This message will appear in the footer of all outgoing customer emails
                            </div>
                        </div>
                    </div>

                    {/* Default template — below Customer Message (same card pattern) */}
                    <div className="card shadow-lg mb-4 template-card">
                        <div className="card-body">
                            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-1">
                                <i className="bi bi-file-earmark-check" style={{ color: "#1E66FF" }} />
                                Default Template
                            </h6>
                            {/* <p className="text-muted small mb-3">
                                Choose whether this email template should be used as the default
                            </p>
                            <p className="mb-3" style={{ color: "#374151", fontSize: "0.9375rem", lineHeight: 1.5 }}>
                                You selected default one is show the email inside
                            </p> */}
                            <div className="form-check email-default-template-check d-flex align-items-center gap-2 mt-2">
                                <input
                                    id="emailMakeDefaultTemplate"
                                    className="form-check-input flex-shrink-0"
                                    type="checkbox"
                                    checked={makeDefaultTemplate}
                                    onChange={(e) => setMakeDefaultTemplate(e.target.checked)}
                                />
                                <label className="form-check-label mb-0" htmlFor="emailMakeDefaultTemplate" style={{ color: "#1e3a5f" }}>
                                    Make this as default template
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Social (layout/buttons aligned with SignageInvoiceTemplateAddEdit) */}
                    <div className="card shadow-lg mb-4 template-card">
                        <div className="card-body">
                            <h6 className="fw-semibold d-flex align-items-center gap-2 mb-1">
                                <i className="bi bi-share" style={{ color: "#1E66FF" }} />
                                Social Media Links
                            </h6>
                            <p className="text-muted small mb-4">
                                Add your social profiles so customers can follow you directly from emails
                            </p>

                            <SocialUrlRow
                                id="em-li"
                                label="LinkedIn"
                                iconClass="bi bi-linkedin"
                                badgeStyle={{ background: "#0A66C2" }}
                                placeholder="https://linkedin.com/company/your-company"
                                value={form.linkedin}
                                onChange={(v) => setField("linkedin", v)}
                            />
                            <SocialUrlRow
                                id="em-fb"
                                label="Facebook"
                                iconClass="bi bi-facebook"
                                badgeStyle={{ background: "#1877F2" }}
                                placeholder="https://facebook.com/your-page"
                                value={form.facebook}
                                onChange={(v) => setField("facebook", v)}
                            />
                            <SocialUrlRow
                                id="em-ig"
                                label="Instagram"
                                iconClass="bi bi-instagram"
                                badgeStyle={{
                                    background:
                                        "linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)",
                                }}
                                placeholder="https://instagram.com/your-handle"
                                value={form.instagram}
                                onChange={(v) => setField("instagram", v)}
                            />
                            <SocialUrlRow
                                id="em-x"
                                label="Twitter / X"
                                iconClass="bi bi-twitter-x"
                                badgeStyle={{ background: "#000" }}
                                placeholder="https://x.com/your-handle"
                                value={form.twitter}
                                onChange={(v) => setField("twitter", v)}
                            />
                        </div>
                    </div>

                    <div className="email-template-actions d-flex align-items-center flex-wrap pb-4">
                        <button type="submit" className="btn-email-save" disabled={saving || uploadingLogo || deletingLogo || initialLoading}>
                            <i className="bi bi-envelope" aria-hidden />
                            {saving ? "Saving..." : isEditMode ? "Update Email Template" : "Save Email Template"}
                        </button>
                        <button type="button" className="btn-email-reset" onClick={handleReset} disabled={saving || uploadingLogo || deletingLogo || initialLoading}>
                            Reset
                        </button>
                    </div>
                </form>

                <style>{`
        .template-card { border-radius: 19px; }
        .dropzone {
          height: 220px;
          border: 2px dashed #cfd6e4;
          border-radius: 16px;
          background: #fafcff;
        }
        .logo-preview-wrap {
          min-height: 220px;
          border: 2px dashed #cfd6e4;
          border-radius: 16px;
          background: #fafcff;
        }
        .logo-preview {
          width: 180px;
          height: 130px;
          border-radius: 12px;
          background: #f8fafc;
          border: 1px solid #e6e9ef;
          overflow: hidden;
        }
        .always-border, .always-border:focus {
          border: 2px solid #cfd6e4 !important;
          box-shadow: none !important;
          background: #f7f8fc;
        }

        /* Social rows — single rounded shell, prefix flush left, light field (ref image 1) */
        .social-field-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #111827;
          margin-bottom: 0.35rem;
        }
        .social-field-label i {
          font-size: 1rem;
          color: #111827;
        }
        .social-input-group {
          display: flex;
          align-items: stretch;
          max-width: 36rem;
          height: 48px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          background: #f3f4f6;
        }
        .social-input-group:focus-within {
          border-color: #d1d5db;
          box-shadow: 0 0 0 2px rgba(30, 102, 255, 0.12);
        }
        .social-input-prefix {
          width: 48px;
          min-width: 48px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff !important;
          font-size: 1.15rem;
          border-radius: 0;
        }
        .social-input-prefix i {
          color: #fff !important;
        }
        .social-input-field {
          flex: 1;
          min-width: 0;
          border: none;
          background: #f9fafb;
          padding: 0 14px;
          font-size: 0.9375rem;
          color: #111827;
        }
        .social-input-field::placeholder {
          color: #9ca3af;
        }
        .social-input-field:focus {
          outline: none;
          background: #fff;
        }

        /* Actions — Save: pill + shadow (ref image 3); Reset: gray fill, md radius, smaller (ref image 3–4) */
        .email-template-actions {
          gap: 0.75rem;
        }
        .btn-email-save {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: none;
          background: #1E66FF;
          color: #fff !important;
          font-weight: 600;
          font-size: 0.9375rem;
          padding: 0.75rem 1.5rem;
          border-radius: 14px;
          box-shadow: 0 4px 14px rgba(30, 102, 255, 0.35);
          transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;
        }
        .btn-email-save:hover:not(:disabled) {
          background: #1856d9;
          box-shadow: 0 6px 18px rgba(30, 102, 255, 0.42);
        }
        .btn-email-save:active:not(:disabled) {
          transform: translateY(1px);
        }
        .btn-email-save:disabled {
          opacity: 0.65;
          cursor: not-allowed;
        }
        .btn-email-reset {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #f9fafb;
          color: #111827;
          border: 1px solid #e5e7eb;
          font-weight: 400;
          font-size: 0.9375rem;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          line-height: 1.25;
          transition: background 0.15s ease, border-color 0.15s ease;
        }
        .btn-email-reset:hover:not(:disabled) {
          background: #f3f4f6;
          border-color: #d1d5db;
        }
        .btn-email-reset:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .email-default-template-check .form-check-input {
          width: 1.15rem;
          height: 1.15rem;
          margin-top: 0;
          border-width: 2px;
          cursor: pointer;
        }
        .email-default-template-check .form-check-input:checked {
          background-color: #1E66FF;
          border-color: #1E66FF;
        }
        .email-default-template-check .form-check-label {
          cursor: pointer;
          font-weight: 500;
        }
      `}</style>
            </div>
        </div>
    );
}
